import { GoTrueClient } from "@supabase/auth-js";
import { PostgrestClient } from "@supabase/postgrest-js";

import type { Realtime } from "./realtime-client.ts";
import type { Storage } from "./storage.ts";
import type { Environment } from "../environment.ts";
import type { Database } from "@estate-platform/db/database";
import type { FunctionsClient } from "@supabase/functions-js";

export interface ApiClient {
  readonly auth: GoTrueClient;
  readonly db: PostgrestClient<Database>;
  readonly realtime: () => Promise<Realtime | null>;
  readonly functions: () => Promise<FunctionsClient>;
  readonly storage: () => Promise<Storage>;
  readonly feedUrl: (token: string) => string;
}

export interface Bearer {
  readonly key: string;
  readonly token: () => Promise<string | null>;
  readonly isKeyBearer: boolean;
}

const isNewApiKey = (key: string): boolean =>
  key.startsWith("sb_publishable_") || key.startsWith("sb_secret_");

const withAuth =
  ({ key, token, isKeyBearer }: Bearer): typeof fetch =>
  async (input, init) => {
    const headers = new Headers(init?.headers);
    if (!headers.has("apikey")) {
      headers.set("apikey", key);
    }
    const bearer = (await token()) ?? (isKeyBearer ? key : null);
    if (bearer !== null && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${bearer}`);
    }
    return fetch(input, { ...init, headers });
  };

const endpoint = (base: URL, path: string): string => {
  const url = new URL(path, base);
  return url.href;
};

const createAuth = (base: URL, key: string): GoTrueClient =>
  new GoTrueClient({
    url: endpoint(base, "auth/v1"),
    headers: { Authorization: `Bearer ${key}`, apikey: key },
    storageKey: `sb-${base.hostname.split(".", 1)[0] ?? ""}-auth-token`,
    detectSessionInUrl: false,
    persistSession: true,
    autoRefreshToken: true,
    experimental: { passkey: true },
  });

const sessionToken = (auth: GoTrueClient) => async (): Promise<string | null> => {
  const { data } = await auth.getSession();
  return data.session?.access_token ?? null;
};

const openRealtime = async (base: URL, bearer: Bearer, auth: GoTrueClient): Promise<Realtime> => {
  const { createRealtime } = await import("./realtime-client.ts");
  const url = new URL("realtime/v1", base);
  url.protocol = url.protocol.replace("http", "ws");
  return createRealtime({ url, key: bearer.key, token: bearer.token, fetch: withAuth(bearer), auth });
};

// WHY: first paint never needs the socket; it loads once, when a page first watches for changes, and a failed load is retried on the next watch.
const loadRealtime = (base: URL, bearer: Bearer, auth: GoTrueClient): (() => Promise<Realtime | null>) => {
  let loading: Promise<Realtime> | undefined;
  return async () => {
    loading ??= openRealtime(base, bearer, auth);
    try {
      return await loading;
    } catch {
      loading = undefined;
      return null;
    }
  };
};

const loadFunctions = (base: URL, bearer: Bearer) => async (): Promise<FunctionsClient> => {
  const { FunctionsClient: Client } = await import("@supabase/functions-js");
  return new Client(endpoint(base, "functions/v1"), {
    customFetch: withAuth({ ...bearer, isKeyBearer: !isNewApiKey(bearer.key) }),
  });
};

const loadStorage = (base: URL, bearer: Bearer) => async (): Promise<Storage> => {
  const { createStorage } = await import("./storage.ts");
  return createStorage(endpoint(base, "storage/v1/"), bearer);
};

// WHY: the supabase-js umbrella client ships storage and functions with every page; composing its parts keeps them out of the first load.
export const createApiClient = (environment: Environment): ApiClient => {
  const raw = environment.VITE_SUPABASE_URL;
  const base = new URL(raw.endsWith("/") ? raw : `${raw}/`);
  const key = environment.VITE_SUPABASE_PUBLISHABLE_KEY;
  const auth = createAuth(base, key);
  const bearer: Bearer = { key, token: sessionToken(auth), isKeyBearer: true };
  return {
    auth,
    db: new PostgrestClient<Database>(endpoint(base, "rest/v1"), { schema: "api", fetch: withAuth(bearer) }),
    realtime: loadRealtime(base, bearer, auth),
    functions: loadFunctions(base, bearer),
    storage: loadStorage(base, bearer),
    feedUrl: (token) => endpoint(base, `functions/v1/calendar-feed/${token}.ics`),
  };
};
